/**
 * Pure state for the launch wizard's "Validate your signature" step.
 *
 * Two sources:
 *   - mailbox: the primary sender's own `signature_html`. Nothing is sent
 *     with the campaign (each rotating inbox keeps using its own); an edit
 *     is saved back to that mailbox before the wizard moves on.
 *   - mine: the launching user's OWN saved signature (Settings > Signatures,
 *     Momentum/Enterprise/Trial only) -- the one signature that applies no
 *     matter which mailbox(es) a campaign sends from. An edit here is saved
 *     back to that same per-user record (same as editing it in Settings)
 *     before the wizard moves on, and the confirmed text is also
 *     snapshotted onto the campaign as `signature_html` for immutability.
 *
 * The mailbox flow has no dependency on the user's own signature at all,
 * which is what keeps the step working on a plan without
 * per_user_signatures (Starter/Free).
 */
import { hasSignatureContent } from '@/lib/signatureHtml'
import type { EmailSignature } from '@/types/signature'

export type SignatureSource = { kind: 'mailbox' } | { kind: 'mine' }

export interface SignatureStepState {
  source: SignatureSource
  html: string
  mailboxHtml: string
  baseline: string
  dirty: boolean
}

export const SIGNATURE_BLOCKER = 'Add a signature before continuing'

export function initialSignatureStep(
  primarySender: { signature_html?: string | null } | undefined,
): SignatureStepState {
  const mailboxHtml = primarySender?.signature_html ?? ''
  return { source: { kind: 'mailbox' }, html: mailboxHtml, mailboxHtml, baseline: mailboxHtml, dirty: false }
}

export function selectSignatureSource(
  state: SignatureStepState,
  source: SignatureSource,
  mine: EmailSignature | null,
): SignatureStepState {
  if (source.kind === 'mailbox') {
    return { ...state, source, html: state.mailboxHtml, baseline: state.mailboxHtml, dirty: false }
  }
  const html = mine?.html ?? ''
  return { ...state, source, html, baseline: html, dirty: false }
}

export function editSignature(state: SignatureStepState, html: string): SignatureStepState {
  return { ...state, html, dirty: html !== state.baseline }
}

export function markMailboxSaved(state: SignatureStepState): SignatureStepState {
  return { ...state, mailboxHtml: state.html, baseline: state.html, dirty: false }
}

export function signatureStepBlocker(state: SignatureStepState): string | null {
  return hasSignatureContent(state.html) ? null : SIGNATURE_BLOCKER
}

/** What the create payload carries. Mailbox mode sends nothing. */
export function signaturePayload(state: SignatureStepState): { signature_html?: string } {
  if (state.source.kind === 'mailbox') return {}
  return { signature_html: state.html.trim() }
}

export function mailboxSaveNeeded(state: SignatureStepState): boolean {
  return state.source.kind === 'mailbox' && state.dirty && hasSignatureContent(state.html)
}

/** An edit to the user's own signature, not yet saved as their permanent one. */
export function mineSaveNeeded(state: SignatureStepState): boolean {
  return state.source.kind === 'mine' && state.dirty && hasSignatureContent(state.html)
}

/** After `saveMine` succeeds: the edit is now the user's saved signature.
 * Unlike markMailboxSaved, this never touches mailboxHtml -- that still
 * belongs to the mailbox, unrelated to the per-user signature. */
export function markMineSaved(state: SignatureStepState): SignatureStepState {
  return { ...state, baseline: state.html, dirty: false }
}
